import { useCallback, useMemo } from 'react';

import type { DxfMappingRole } from '@frontend/api';

import { roleSelectionState } from '../session/mappingModel';
import type { DxfViewerSession, ViewerMode } from '../session/types';
import { useReadableStore } from '../session/useSessionState';

export type DxfMappingPanelState = {
  /** Roles present on this file, with how many entities carry each. */
  counts: ReadonlyMap<DxfMappingRole, number>;
  /**
   * Roles holding at least one entity that can actually be picked, and so worth
   * offering as a click target.
   */
  selectableRoles: ReadonlySet<DxfMappingRole>;
  /**
   * Roles whose every selectable entity is selected.
   *
   * All or nothing, like `selectedLayers`: a partially-caught role is absent, so
   * `has` answers "is this role selected" outright.
   */
  selectedRoles: ReadonlySet<DxfMappingRole>;
  /** False when the file carries no mapping at all — the empty state. */
  hasMapping: boolean;
  mode: ViewerMode;
  /** False when the file is locked, or no drawing has loaded. */
  canEnterMapping: boolean;
  /** When set, mapping cannot be started or edited. */
  lockReason: string | undefined;
  isMappingColoringOn: boolean;
  onEnterMappingMode: () => void;
  onSelectRole: (role: DxfMappingRole, isAdditive: boolean) => void;
  onHoverRole: (role: DxfMappingRole | null) => void;
  onToggleMappingColoring: () => void;
};

/**
 * Connects the mapping panel to the session. Call only under a ready drawing.
 *
 * Subscribes to the mapping slice rather than to `counts` / `roleIndex`
 * individually: those are methods on the manager (see `createMappingController`)
 * derived only from this slice, so the snapshot is both the notification and
 * the `hasMapping` / `lockReason` read.
 */
export const useDxfMappingPanel = (
  session: DxfViewerSession,
): DxfMappingPanelState => {
  const mapping = useReadableStore(session.mapping);
  const mode = useReadableStore(session.mode);
  const isMappingColoringOn = useReadableStore(
    session.overlays,
    (current) => current.mappingColoring,
  );
  const selection = useReadableStore(session.selection);
  const model = useReadableStore(session.model);
  const hiddenLayers = useReadableStore(session.hiddenLayers);

  // `roleIndex` is memoised against the assignments it was built from, so its
  // identity is a sound dependency: it only changes when the mapping does.
  const roleIndex = session.mapping.roleIndex();
  const { selectableRoles, selectedRoles } = useMemo(
    () =>
      model
        ? roleSelectionState(selection, roleIndex, model, hiddenLayers)
        : {
            selectableRoles: new Set<DxfMappingRole>(),
            selectedRoles: new Set<DxfMappingRole>(),
          },
    [selection, roleIndex, model, hiddenLayers],
  );

  const onEnterMappingMode = useCallback(
    () => session.mode.set('mapping'),
    [session],
  );

  const onSelectRole = useCallback(
    (role: DxfMappingRole, isAdditive: boolean) =>
      session.mapping.selectRole(role, isAdditive ? 'add' : 'replace'),
    [session],
  );

  const onHoverRole = useCallback(
    (role: DxfMappingRole | null) => session.mapping.setHoveredRole(role),
    [session],
  );

  const onToggleMappingColoring = useCallback(
    () => session.overlays.toggle('mappingColoring'),
    [session],
  );

  return {
    counts: session.mapping.counts(),
    selectableRoles,
    selectedRoles,
    // Read off the effective mapping rather than off `counts`: a mapping every one
    // of whose entities is unstitched still counts as a mapping, and the panel
    // should not offer to create one that already exists.
    hasMapping: mapping.draft !== null || mapping.saved !== null,
    mode,
    canEnterMapping: model !== null && mapping.lockReason === undefined,
    lockReason: mapping.lockReason,
    isMappingColoringOn,
    onEnterMappingMode,
    onSelectRole,
    onHoverRole,
    onToggleMappingColoring,
  };
};
